import { CreditCard, XCircle } from 'lucide-react';
import { Button } from '@ncoder/ui';
import { MemberAvatar } from '@/components/atoms/org-admin/MemberAvatar';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';
import { cn } from '@/lib/utils';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import { seatPlanLabel } from '@/services/org-admin';
import type { BillingPlan } from '@/services/org-admin';
import type { MemberAllowance } from '@/services/org-admin/allowance';
import { BudgetMeter } from './BudgetMeter';
import { NO_PERMISSION_REASON } from './types';

/**
 * One seat's billing state.
 *
 * Joined in the panel from three sources rather than one: `GET /billing` for billing mode and
 * subscription status, the members list for identity, and the usage rollup for budget and spend.
 *
 * Every field shown is a fact straight from the API — including the plan, which the billing endpoint
 * now reports per member. It used to be inferred from the seat's budget, which meant a topped-up seat
 * read as `Custom`; that inference is gone (services/org-admin/seatPlan.ts).
 */

export interface BillingSeat {
  userId: string;
  email: string;
  /** Straight from `GET /billing` — never derived from the budget. */
  plan: BillingPlan;
  /** `null` when the seat has never purchased, or was downgraded after a cancellation. */
  billingMode: 'recurring' | null;
  /** Stripe's status string, or `"none"`. */
  subscriptionStatus: string;
  /** When the plan renews or auto-resets. `null` for a free or never-billed seat. */
  planCycleEndsAt: string | null;
  /** From the usage report — absent if the seat is not in it. */
  budgetUsd: number | null;
  spendUsd: number | null;
  /**
   * The seat's allowance read through `memberAllowance` — total, what is left this cycle, and the
   * plan/credits split when the backend sent it (#327). `null` when the seat is not in the report.
   */
  allowance: MemberAllowance | null;
  /**
   * True once a cancellation is scheduled — from `GET /billing`'s `cancel_at_period_end` when
   * available (persists across reload, and catches a cancellation made in the Stripe dashboard or
   * portal, not just through this app), OR'd with an in-session flag set immediately after our own
   * cancel call. The in-session half still matters: our cancel endpoint doesn't stamp the field
   * itself, only Stripe's `customer.subscription.updated` webhook does, so the API can briefly still
   * say `false` right after a successful cancel here.
   */
  cancellationScheduled: boolean;
  /** The paid plan whose subscription ended, and when. Both `null` unless it has ended. */
  endedPlan: BillingPlan | null;
  planEndedAt: string | null;
}

/**
 * The subscription ended and nothing has been bought since. The PLAN part of the seat's allowance
 * is $0 — not Free's allowance — so it reads as a problem rather than as an ordinary free seat.
 * Any credit the member holds survives the ending (#327): see `creditLeftAfterEnd`.
 */
export function hasEnded(seat: Pick<BillingSeat, 'planEndedAt'>): boolean {
  return seat.planEndedAt !== null;
}

/** Credit an ended seat can still spend, or `null` when the backend did not say. */
export function creditLeftAfterEnd(seat: Pick<BillingSeat, 'allowance'>): number | null {
  return seat.allowance?.creditRemainingUsd ?? null;
}

/**
 * The Allowance and Available cells, shared by the table row and the card.
 *
 * "Available" is the figure an admin scans for, so it is the bold one and the one with the meter.
 * Both are on the seat's OWN cycle — the backend computes the split against `spend_usd_cycle`,
 * which is the clock the member is paused on. An ended seat whose plan part is $0 but still holds
 * credit shows that credit, never "No allowance".
 */
function AllowanceCells({ seat }: { seat: BillingSeat }): { allowance: React.ReactNode; available: React.ReactNode } {
  const a = seat.allowance;
  if (!a) return { allowance: <span className="oa-meta">—</span>, available: <span className="oa-meta">—</span> };

  if (a.totalUsd <= 0) {
    return {
      allowance: <span className="oa-meta">{hasEnded(seat) ? 'No allowance' : 'No limit'}</span>,
      available: <span className="oa-meta">—</span>,
    };
  }

  const exhausted = a.remainingUsd !== null && a.remainingUsd <= 0;
  const used = a.remainingUsd === null ? null : Math.max(0, a.totalUsd - a.remainingUsd);

  return {
    allowance: (
      <>
        {formatCurrencyUsd(a.totalUsd)}
        {a.hasBreakdown && (
          <span className="oa-meta block">
            plan {formatCurrencyUsd(a.planBudgetUsd!)} + credits {formatCurrencyUsd(a.creditBalanceUsd!)}
          </span>
        )}
      </>
    ),
    available:
      a.remainingUsd === null ? (
        <span className="oa-meta">—</span>
      ) : (
        <>
          <span className={cn('font-semibold', exhausted && 'text-destructive-strong')}>
            {formatCurrencyUsd(a.remainingUsd)}
          </span>
          {used !== null && (
            <BudgetMeter size="sm" className="ml-auto mt-1 w-20 max-w-full" spentUsd={used} budgetUsd={a.totalUsd} label={seat.email} />
          )}
        </>
      ),
  };
}

/** Whether there is a live subscription that could be cancelled. */
export function hasLiveSubscription(seat: BillingSeat): boolean {
  return (
    seat.billingMode === 'recurring' &&
    seat.subscriptionStatus !== 'none' &&
    seat.subscriptionStatus !== 'canceled'
  );
}

/**
 * Which plan this seat is on — the question an admin scanning this table is actually asking.
 *
 * A dash when the plan is a value this build cannot name, which only happens against a backend that
 * predates the field. Better an admitted gap than a chip with no text in it.
 */
function billingLabel(seat: BillingSeat): { text: string; tone: 'neutral' | 'good' | 'warn' | 'bad' } {
  if (hasEnded(seat)) return { text: 'No plan', tone: 'bad' };
  const label = seatPlanLabel(seat.plan);
  if (label === null) return { text: '—', tone: 'neutral' };
  return { text: label, tone: seat.plan === 'free' ? 'neutral' : 'good' };
}

/**
 * 'Monthly', as a second line under the plan — or nothing for a free seat.
 *
 * Every paid seat is a subscription now, so this no longer distinguishes modes; it distinguishes
 * PAID from free, which is still worth a line under the plan chip.
 */
function modeLabel(seat: BillingSeat): string | null {
  if (seat.billingMode === null) return null;
  return 'Monthly';
}

/**
 * The verb for a cycle boundary, and how it reads once that boundary has passed.
 *
 * The past-tense forms are separate strings rather than a suffix because composing them
 * mechanically produces nonsense: prefixing 'Renews ' onto a label of 'due to renew' reads
 * 'Renews due to renew'.
 */
const CYCLE_COPY: Record<'recurring', { future: string; overdue: string }> = {
  recurring: { future: 'Renews', overdue: 'Renewal due' },
};

/**
 * A cycle-boundary date, in the module's house style for dates: a relative label in the cell, the
 * absolute date as the tooltip (see `formatExpiry` in InvitationRow.tsx).
 *
 * `isPast` is defensive, not decorative. Stripe rolls the period before
 * `customer.subscription.updated` reaches our backend, so this date is legitimately in the past for a
 * short window and a reader that assumed otherwise would render "in -1d".
 *
 * The NaN guard matters for the same class of reason: an unparseable timestamp otherwise falls
 * straight through the day comparisons and renders the literal string "in NaNd".
 */
export function formatCycleEnd(iso: string): { label: string; isPast: boolean; title: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const absolute = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const days = Math.round((date.getTime() - Date.now()) / 86_400_000);
  const label = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days}d`;

  return { label, isPast: days < 0, title: absolute };
}

/**
 * The mode and the cycle date under the plan chip. No extra column for either.
 *
 * Two stacked lines rather than "One-time · Resets in 4d" on one. Joined inline they wrapped
 * mid-phrase in this column's width, leaving a dangling "One-time ·" above the rest — the same
 * vertical space as two clean lines, and worse to read.
 */
function CycleLine({ seat }: { seat: BillingSeat }) {
  if (hasEnded(seat)) {
    const ended = formatCycleEnd(seat.planEndedAt!);
    const plan = seat.endedPlan ? seatPlanLabel(seat.endedPlan) : null;
    return (
      <time
        dateTime={seat.planEndedAt!}
        className="oa-meta mt-0.5 block font-medium tabular-nums text-destructive-strong"
      >
        {plan ?? 'Plan'} ended{ended ? ` ${ended.title}` : ''}
      </time>
    );
  }

  const mode = modeLabel(seat);
  if (!mode) return null;

  const cycle = seat.planCycleEndsAt ? formatCycleEnd(seat.planCycleEndsAt) : null;
  const copy = seat.billingMode ? CYCLE_COPY[seat.billingMode] : null;

  return (
    <>
      <span className="oa-meta mt-0.5 block">{mode}</span>
      {cycle && copy && (
        <time
          dateTime={seat.planCycleEndsAt!}
          title={cycle.title}
          className={cn(
            'oa-meta block tabular-nums',
            seat.cancellationScheduled && 'font-medium text-warning-strong',
          )}
        >
          {/* A cancelled seat does not renew, so "Renews in 4d" would be a false promise. The
              absolute date here, the relative one in the status chip beside it. */}
          {seat.cancellationScheduled
            ? `Ends ${cycle.title} · won’t renew`
            : cycle.isPast
              ? copy.overdue
              : `${copy.future} ${cycle.label}`}
        </time>
      )}
    </>
  );
}

/**
 * How a seat's subscription reads, in plain language.
 *
 * `subscription_status` is Stripe's raw string, which is a vocabulary the admin never chose —
 * `past_due` and `incomplete_expired` mean nothing to most readers. Each is translated, and anything
 * unrecognised falls through to the raw value rather than being hidden.
 */

function statusLabel(
  seat: BillingSeat,
): { text: string; tone: 'neutral' | 'good' | 'warn' | 'bad'; raw?: boolean } | null {
  const status = seat.subscriptionStatus;
  if (hasEnded(seat)) return { text: 'Subscription ended', tone: 'bad' };
  if (seat.billingMode === null && status === 'none') return null;

  switch (status) {
    case 'active': {
      if (!seat.cancellationScheduled) return { text: 'Active', tone: 'good' };
      const cycle = seat.planCycleEndsAt ? formatCycleEnd(seat.planCycleEndsAt) : null;
      return {
        text: cycle && !cycle.isPast ? `Ends ${cycle.label}` : 'Ends at period end',
        tone: 'warn',
      };
    }
    case 'past_due':
      return { text: 'Past due', tone: 'bad' };
    case 'unpaid':
      return { text: 'Unpaid', tone: 'bad' };
    case 'canceled':
      return { text: 'Cancelled', tone: 'neutral' };
    case 'trialing':
      return { text: 'Trialing', tone: 'good' };
    case 'incomplete':
    case 'incomplete_expired':
      return { text: 'Payment incomplete', tone: 'warn' };
    case 'none':
      return null;
    default:
      // An unmapped Stripe status: shown rather than hidden, with its underscores softened.
      return { text: status.replace(/_/g, ' '), tone: 'neutral' as const, raw: true };
  }
}

const TONE_CLASS: Record<'neutral' | 'good' | 'warn' | 'bad', string> = {
  neutral: 'border-border/70 bg-muted/60 text-muted-foreground',
  good: 'border-success/25 bg-success/10 text-success-strong',
  warn: 'border-warning/25 bg-warning/10 text-warning-strong',
  bad: 'border-destructive/25 bg-destructive/10 text-destructive-strong',
};

function Chip({
  text,
  tone,
  raw,
}: {
  text: string;
  tone: 'neutral' | 'good' | 'warn' | 'bad';
  /** An untranslated Stripe status, which needs case-fixing; curated labels do not. */
  raw?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium',
        // `capitalize` on a curated label rendered "One-time" as "One-Time".
        raw && 'capitalize',
        TONE_CLASS[tone],
      )}
    >
      {text}
    </span>
  );
}

export interface BillingMemberRowProps {
  seat: BillingSeat;
  canManage: boolean;
  /** A mutation is in flight for this seat — blocks a second charge for the same person. */
  isBusy: boolean;
  onBuyPlan: (seat: BillingSeat) => void;
  onCancel: (seat: BillingSeat) => void;
  onResume: (seat: BillingSeat) => void;
}

function Actions({ seat, canManage, isBusy, onBuyPlan, onCancel, onResume }: BillingMemberRowProps) {
  const live = hasLiveSubscription(seat);
  const disabledReason = canManage ? undefined : NO_PERMISSION_REASON;

  // An ending or ended subscription gets a labelled button rather than an icon: it is the one
  // action on the row the admin needs to take, and an icon alone does not say so.
  const callToAction =
    live && seat.cancellationScheduled
      ? { label: 'Renew', onClick: () => onResume(seat) }
      : hasEnded(seat)
        ? { label: 'Buy a plan', onClick: () => onBuyPlan(seat) }
        : null;

  if (callToAction) {
    return (
      <Button
        type="button"
        size="sm"
        className="h-7 text-[12px]"
        disabled={!canManage || isBusy}
        title={disabledReason}
        aria-label={`${callToAction.label} for ${seat.email}`}
        onClick={callToAction.onClick}
      >
        {callToAction.label}
      </Button>
    );
  }

  return (
    <>
      {/*
        One button, two transactions, and which one it is depends on `live`.

        This used to be hidden entirely for a live subscription, because buying on top of one
        returns 409 `subscription_already_active` and a button whose only outcome is an error is
        worse than no button. That reasoning was right, and the conclusion is now inverted: a live
        subscription is exactly the seat that CAN be repriced (`PUT …/subscriptions/:id/plan`,
        prorated), so hiding the control there hides the only place the feature is reachable.

        The label was also backwards. It said "Change plan" for any seat holding a plan, which
        included ONE-TIME seats — the one case that genuinely cannot be changed (no subscription to
        reprice; the backend rejects it) and whose real remedy is to buy again once the period
        lapses. Keying both label and behaviour off `live` makes the button say what will happen.
      */}
      <IconActionButton
        label={live ? `Change plan for ${seat.email}` : `Buy a plan for ${seat.email}`}
        icon={CreditCard}
        size="sm"
        disabled={!canManage || isBusy}
        disabledReason={disabledReason}
        onClick={() => onBuyPlan(seat)}
      />
      {live && (
        <IconActionButton
          label={`Cancel subscription for ${seat.email}`}
          icon={XCircle}
          tone="destructive"
          size="sm"
          disabled={!canManage || isBusy}
          disabledReason={disabledReason}
          onClick={() => onCancel(seat)}
        />
      )}
    </>
  );
}

/** Table variant — used whenever the panel has room for columns. */
export function BillingMemberRow(props: BillingMemberRowProps) {
  const { seat } = props;
  const billing = billingLabel(seat);
  const status = statusLabel(seat);
  const cells = AllowanceCells({ seat });

  return (
    <tr>
      <td>
        <div className="flex min-w-0 items-center gap-2.5">
          <MemberAvatar email={seat.email} userId={seat.userId} />
          <span className="truncate font-medium">{seat.email}</span>
        </div>
      </td>
      <td>
        <Chip {...billing} />
        <CycleLine seat={seat} />
      </td>
      <td>{status ? <Chip {...status} /> : <span className="oa-meta">—</span>}</td>
      <td className="oa-num">{cells.allowance}</td>
      <td className="oa-num">{cells.available}</td>
      <td>
        <div className="oa-row-actions flex justify-end gap-0.5">
          <Actions {...props} />
        </div>
      </td>
    </tr>
  );
}

/** Card variant — used when the panel is too narrow for six columns. */
export function BillingMemberCard(props: BillingMemberRowProps) {
  const { seat } = props;
  const billing = billingLabel(seat);
  const status = statusLabel(seat);
  const cells = AllowanceCells({ seat });

  return (
    <div className="oa-card-row">
      <div className="flex items-start justify-between gap-3">
        {/* `items-start`, not `items-center`: the text block is three lines now, and a vertically
            centred avatar beside it sits level with the chips rather than with the address. */}
        <div className="flex min-w-0 items-start gap-2.5">
          <MemberAvatar email={seat.email} userId={seat.userId} />
          <div className="min-w-0">
            <span className="block truncate font-medium">{seat.email}</span>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Chip {...billing} />
              {status && <Chip {...status} />}
            </div>
            {/* Not a third chip: three pills in a row on a phone reads as noise, and the mode and
                cycle date are the least urgent of the three. Its own line, so a long "Renews in 12d"
                cannot push the status chip onto a second row. */}
            <CycleLine seat={seat} />
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5">
          <Actions {...props} />
        </div>
      </div>
      {seat.allowance && (
        /* `div`s, not `p`s: the available cell carries the meter, which is a block element, and a
           block inside a `p` is invalid nesting that React warns about on every narrow render. */
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 tabular-nums">
          <div className="oa-meta">Allowance {cells.allowance}</div>
          <div className="text-[13px]">
            {cells.available} <span className="oa-meta">available this cycle</span>
          </div>
        </div>
      )}
    </div>
  );
}

export { billingLabel, modeLabel, statusLabel };
